import React from 'react';
import Router from 'next/router';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import {
  CategoryBadges,
  LinkButton,
  Owners,
  PublishedProductText,
  TimeIndicator,
  User
} from 'components/common';
import {
  CourseLanguageVersionsFlags,
  CoursePicture
} from 'components/courses/common';
import { CoursesAdminActions } from '.';

import type { CourseInView } from './Courses';

interface Props {
  course: CourseInView;
  updateCourses: () => Promise<void>;
}

const CoursesAdminTile = ({ course, updateCourses }: Props) => {
  const {
    language,
    messages,
    serverConfig: { enableCoursePicture },
    theme
  } = useReduxState();

  const handleCourseClick = () => {
    const { adminPermissions, isOwnerOrSuperadmin, slug } = course;

    if (isOwnerOrSuperadmin) {
      Router.push('/admin/courses/[courseSlug]', `/admin/courses/${slug}`);
    } else if (adminPermissions.includes('edit-content')) {
      Router.push(
        '/admin/courses/[courseSlug]/edit',
        `/admin/courses/${slug}/edit`
      );
    } else if (adminPermissions.includes('edit-settings')) {
      Router.push(
        '/admin/courses/[courseSlug]/settings',
        `/admin/courses/${slug}/settings`
      );
    } else if (adminPermissions.includes('manage-users')) {
      Router.push(
        '/admin/courses/[courseSlug]/publish',
        `/admin/courses/${slug}/publish`
      );
    } else if (adminPermissions.includes('review-tasks')) {
      Router.push(
        '/admin/courses/[courseSlug]/follow/users',
        `/admin/courses/${slug}/follow/users`
      );
    } else if (adminPermissions.includes('view-feedback')) {
      Router.push(
        '/admin/courses/[courseSlug]/follow/feedback',
        `/admin/courses/${slug}/follow/feedback`
      );
    } else if (adminPermissions.includes('view-analytics')) {
      Router.push(
        '/admin/courses/[courseSlug]/follow/visitors',
        `/admin/courses/${slug}/follow/visitors`
      );
    }
  };

  const handleCourseKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.target !== ev.currentTarget) return;
    // check if enter or spacebar
    if (ev.keyCode === 13 || ev.keyCode === 32) {
      ev.preventDefault();
      handleCourseClick();
    }
  };

  return (
    <div
      className="course"
      onClick={handleCourseClick}
      onKeyDown={handleCourseKeyDown}
      role="button"
      tabIndex={0}
    >
      {enableCoursePicture ? (
        <div className="picture">
          <CoursePicture
            position={course.picturePosition}
            size={640}
            url={course.picture}
          />
        </div>
      ) : null}
      <div className="content">
        <div className="inner">
          <div className="title">{course.title}</div>

          <CourseLanguageVersionsFlags course={course} onlyPublished={false} />

          <div className="info">
            <div>
              <PublishedProductText
                endDate={course.endDate as string}
                startDate={course.startDate as string}
              />
            </div>
            <div style={{ color: theme.textColorLighter, marginTop: 7 }}>
              <span>
                {getMessageWithPlaceholders({
                  message: messages.courseNumberOfChapters,
                  placeholders: {
                    numberOfChapters: course.chapters?.length || 0
                  }
                })}
              </span>

              <span className="info-divider">{'•'}</span>

              <span>
                {`${messages.commonUpdated} `}
                <TimeIndicator
                  language={language}
                  timeString={course.updated as string}
                />
              </span>
            </div>
          </div>

          <CategoryBadges categories={course.categories} />

          <div className="footer">
            <div className="owners">
              {course.owners?.length ? (
                <>
                  <User
                    showNameOrUsername={false}
                    size="tiny"
                    user={course.owners?.[0]}
                  />

                  <Owners owners={course.owners} />
                </>
              ) : (
                messages.commonNoOwners
              )}
            </div>

            <div className="open">
              <LinkButton
                category="default"
                href={`/courses/${course.slug}${
                  course.chapters.length ? `/${course.chapters[0].slug}` : ''
                }`}
                isRounded={true}
                onClick={(ev) => ev.stopPropagation()}
                size="small"
                style={{ height: 30, padding: '6px 12px 7px' }}
                text={messages.commonOpen}
                tooltip={
                  course.unreadChatCount
                    ? `${course.unreadChatCount} ${messages.coursesUnreadChatMessages}`
                    : ''
                }
                unreadItems={course.unreadChatCount}
              />
            </div>
          </div>
        </div>

        <div className="actions">
          <CoursesAdminActions course={course} />
        </div>
      </div>

      <style jsx>{`
        .course {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          gap: 15px;
          outline: 0;
          position: relative;
        }
        .course {
          cursor: pointer;
          transition: box-shadow 0.3s;
        }
        .course:focus,
        .course:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .course:focus-visible {
          outline: revert;
        }
        .picture {
          border-radius: ${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0;
          height: 140px;
          overflow: hidden;
          position: relative;
        }
        .inner {
          display: flex;
          flex-direction: column;
          min-height: 160px;
          padding: 10px 15px;
        }
        .title {
          color: ${theme.textColor};
          display: -webkit-box;
          font-size: 1.6rem;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        .info {
          align-items: center;
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1;
          margin-top: 8px;
        }
        .info-divider {
          margin: 0 7px;
        }
        .owners {
          align-items: center;
          color: ${theme.textColorLight};
          display: inline-flex;
          flex-shrink: 1;
          font-size: 1.3rem;
          font-weight: 600;
          gap: 5px;
        }
        .footer {
          align-items: center;
          display: flex;
          gap: 20px;
          justify-content: space-between;
          margin-top: auto;
          padding-top: 20px;
        }
        .actions {
          border-top: 1px solid ${theme.lightGrey};
          padding: 10px 15px;
        }
      `}</style>
    </div>
  );
};

export default CoursesAdminTile;
